import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, GiftIcon, ShieldCheckIcon } from 'lucide-react';
import { Logo } from './Logo';
import { roleOptions } from '../data/profile';
interface ProfileScreenProps {
  initialRole: string | null;
  onContinue: (roleId: string | null) => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
export function ProfileScreen({
  initialRole,
  onContinue
}: ProfileScreenProps) {
  const [role, setRole] = useState<string | null>(initialRole);
  return <div className="relative flex h-full flex-col bg-mist-50" data-id="element-445" data-mp="1iadcqb3uyke8">
      <div className="flex-1 overflow-y-auto px-6 pb-6 pt-6 no-scrollbar md:pt-11" data-id="element-446" data-mp="1nast7bjfs80f">
        <div className="flex items-center gap-2.5" data-id="element-447" data-mp="1rc4krmlvf3b9">
          <Logo width={22} height={22} data-id="element-448" data-mp="1ratixsbzk5ms" />
          <span className="text-[12px] font-semibold tracking-[0.18em] text-navy-900" data-id="element-449" data-mp="1r9s4rezob30u">IVF PULSE</span>
        </div>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.1
      }} className="mt-8 text-[11px] font-semibold uppercase tracking-[0.22em] text-pulse-teal" data-id="element-450" data-mp="3t37w6e3l4lwv">
          First, a little about you
        </motion.p>
        <motion.h2 initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.15,
        ease
      }} className="mt-3 font-serif text-[28px] leading-[1.12] text-navy-900" data-id="element-451" data-mp="3ayubadl8g261">
          Which best describes your role in fertility care?
        </motion.h2>

        <fieldset className="mt-6" data-id="element-452" data-mp="2irfp67ea3y2">
          <legend className="sr-only" data-id="element-453" data-mp="2jsp768riido">Your role in fertility care</legend>
          <div className="overflow-hidden rounded-3xl border border-navy-900/[0.08] bg-white" data-id="element-454" data-mp="3toaljsmpz8zn">
            {roleOptions.map((o, i) => {
            const checked = role === o.id;
            return <motion.label key={o.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.25 + i * 0.05,
              ease
            }} className={`flex cursor-pointer items-center gap-3.5 px-4 py-3.5 transition ${i > 0 ? 'border-t border-navy-900/[0.06]' : ''} ${checked ? 'bg-pulse-teal/[0.07]' : 'hover:bg-mist-50'}`} data-id="element-455" data-mp="2iayjnnait0g4" data-mpi={i}>
                  <input type="radio" name="role" value={o.id} checked={checked} onChange={() => setRole(o.id)} className="peer sr-only" data-id="element-456" data-mp="10b8zev73yday" />
                  <span aria-hidden="true" className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition peer-focus-visible:ring-2 peer-focus-visible:ring-pulse-teal peer-focus-visible:ring-offset-2 ${checked ? 'border-navy-900 bg-navy-900' : 'border-navy-900/20'}`} data-id="element-457" data-mp="cnr26k52qn0l">
                    {checked && <span className="h-1.5 w-1.5 rounded-full bg-white" data-id="element-458" data-mp="3k88q9blvkqpe" />}
                  </span>
                  <span className={`text-[15px] leading-snug ${checked ? 'font-medium text-navy-900' : 'text-navy-800'}`} data-id="element-459" data-mp="3g76y1eqdlewx">
                    {o.label}
                  </span>
                </motion.label>;
          })}
          </div>
        </fieldset>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.55,
        ease
      }} className="mt-4 flex gap-3 rounded-2xl bg-pulse-teal/[0.08] p-4" data-id="element-460" data-mp="70fbd8r4gudv">
          <GiftIcon className="mt-0.5 h-4 w-4 shrink-0 text-pulse-teal" data-id="element-461" data-mp="gpyv0gmnss9s" />
          <div data-id="element-462" data-mp="bsyqia5gjxks">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-pulse-teal" data-id="element-463" data-mp="bs73votu9a6n">The reward</p>
            <p className="mt-1 text-[13px] leading-relaxed text-navy-800" data-id="element-464" data-mp="qmtub1qrawp4">
              We’ll help you compare your perspective with relevant colleagues, where sufficient responses are available.
            </p>
          </div>
        </motion.div>

        <p className="mt-4 flex items-center gap-2 text-[11px] text-slate-400" data-id="element-465" data-mp="2k6vhzvv22nj3">
          <ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-466" data-mp="2oynawd1op5ce" />
          Anonymous · used only to tailor your comparisons
        </p>
      </div>

      <div className="border-t border-navy-900/5 bg-white/90 p-5 backdrop-blur-md" data-id="element-467" data-mp="2twf3zae2exi6">
        <button type="button" onClick={() => onContinue(role)} disabled={!role} className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-navy-900 text-[15px] font-semibold text-white transition hover:bg-navy-800 disabled:cursor-not-allowed disabled:bg-navy-900/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-pulse-teal focus-visible:ring-offset-2" data-id="element-468" data-mp="1wahq5qamrgck">
          Continue
          <ArrowRightIcon className="h-4 w-4" data-id="element-469" data-mp="3hpdox0ez01yd" />
        </button>
        <button type="button" onClick={() => onContinue(null)} className="mx-auto mt-2 block rounded-full px-3 py-1.5 text-[12px] text-slate-500 transition hover:text-navy-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-900/30" data-id="element-470" data-mp="3mksz9idrrfq1">
          Prefer not to say
        </button>
      </div>
    </div>;
}